import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BookOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  FolderOpenOutlined,
  LeftOutlined,
  MessageOutlined,
  PlusOutlined,
  ProjectOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { Spin } from "antd";
import { Button, Table } from "@/design-system";
import countries from "../../constants/countries";
import { API_CHECKLIST_TASK } from "../../constants";
import nodeAPI from "../../service/ApiServiceNode";
import { fetchLeaveRequestHistory } from "../../service/leaveRequestHistory";
import { ROLES } from "../../service/RoleService";
import { formatAccountingDate } from "../../utils/dateUtils";
import logger from "../../utils/logger";
import {
  formatNetworkingEventDateRange,
  getUpcomingFinanceUpdateEvents,
  getUpcomingHrUpdateEvents,
  getUpcomingNetworkingEvents,
} from "../Calendar/networkingEvents";
import { getTaskStatusMeta } from "../Tasks/taskLifecycle";
import DashboardNotificationBell from "./DashboardNotificationBell";
import "./StaffDashboard.scss";

const DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const LONG_DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

const TIME_FORMAT = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
});

const DASHBOARD_TABLE_SCROLL = { x: "max-content" };
export const PROJECT_SHOWCASE_URL = "https://atlas-api-production-e479.up.railway.app/api/public/showcase?limit=6";
export const PROJECT_SHOWCASE_CACHE_KEY = "hrp.staffDashboard.projectShowcase.v10";
export const PROJECT_SHOWCASE_CACHE_MS = 24 * 60 * 60 * 1000;
export const PROJECT_SHOWCASE_REMOTE_MAX_ITEMS = 6;
export const PROJECT_SHOWCASE_MAX_ITEMS = PROJECT_SHOWCASE_REMOTE_MAX_ITEMS + 1;
export const PROJECT_SHOWCASE_PAGE_SIZE = 3;
const PROJECT_SHOWCASE_AUTO_SLIDE_MS = 5000;
const PROJECT_SHOWCASE_INTERACTION_PAUSE_MS = 8000;
export const EMPLOYEE_HANDBOOK_SHOWCASE_PROJECT = Object.freeze({
  key: "mediatropy-employee-handbook-2026-2027",
  title: "Mediatropy Employee Handbook 2026/2027",
  summary: "Our official guide to employment, leave, benefits, wellbeing, conduct, security, and working at Mediatropy.",
  department: "People & Culture",
  status: "Live",
  sourceUrl: "https://hrp.mediatropy.com/wiki/page/mediatropy-employee-handbook-2026-2027",
  heroUrl: "/assets/images/project-showcase/employee-handbook-2026-2027.webp",
  videoUrl: null,
  videoPosterUrl: null,
  fallbackHeroUrl: null,
  actionLabel: "View Handbook",
});
const PROJECT_SHOWCASE_PALETTES = [
  { background: "#111111", accent: "#FED230", secondary: "#F6F6F4" },
  { background: "#263238", accent: "#7DD3FC", secondary: "#F8FAFC" },
  { background: "#312E81", accent: "#FDE68A", secondary: "#EEF2FF" },
  { background: "#064E3B", accent: "#A7F3D0", secondary: "#F8FAFC" },
  { background: "#7F1D1D", accent: "#FCA5A5", secondary: "#FFF7ED" },
  { background: "#3F3F46", accent: "#E4E4E7", secondary: "#FEF3C7" },
];

const ANNUAL_LEAVE_KEYS = ["annual", "carryover", "carry over", "adjustment", "anniversary"];

const getByPath = (value, path) =>
  path.reduce((current, key) => current?.[key], value);

const pickList = (value, paths) => {
  for (const path of paths) {
    const candidate = getByPath(value, path);
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
};

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") ?? null;

const numberOrNull = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const pluralize = (value, singular, plural = `${singular}s`) =>
  Number(value) === 1 ? singular : plural;

export const getPendingActionLabel = (value, singular, plural = `${singular}s`) =>
  `${value} ${pluralize(value, singular, plural)} pending`;

export const normalizeDashboardRoles = (roles) => {
  const roleList = Array.isArray(roles)
    ? roles
    : typeof roles === "string"
      ? roles.split(",")
      : roles && typeof roles === "object"
        ? Object.values(roles)
        : [];

  return roleList
    .map((role) => typeof role === "string" ? role : pickFirst(role?.name, role?.role, role?.code, role?.key))
    .filter(Boolean)
    .map((role) => String(role).trim())
    .filter(Boolean);
};

const normalizeDashboardText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isManagementDepartmentLabel = (department) =>
  /^management(\s+department)?(\s+in\s+.+)?$/.test(department);

export const getDashboardEventAudience = (user = {}, departmentLabel = null) => {
  const roleSet = new Set(normalizeDashboardRoles(user?.roles));
  const department = normalizeDashboardText(
    pickFirst(
      departmentLabel,
      user?.department?.name,
      user?.departmentName,
      user?.department,
      user?.office_department,
    ),
  );
  const isManagement = [
    ROLES.ROLE_MANAGEMENT,
    ROLES.ROLE_OWNER,
    ROLES.ROLE_SUPER_ADMIN,
  ].some((role) => roleSet.has(role)) || isManagementDepartmentLabel(department);
  const isFinance = roleSet.has(ROLES.ROLE_FINANCE) || department.includes("finance");
  const isHr = roleSet.has(ROLES.ROLE_HR) || /\bhr\b|human resources|people/.test(department);

  return {
    isManagement,
    isFinance,
    isHr,
  };
};

const DASHBOARD_EVENT_STREAM_LABELS = {
  marketing: "Marketing",
  finance: "Finance",
  hr: "HR",
};

const getDashboardEventSortTime = (event = {}) => {
  const date = new Date(`${event.start}T00:00:00`);
  return Number.isNaN(date.getTime()) ? Number.MAX_SAFE_INTEGER : date.getTime();
};

export const getCombinedDashboardEvents = (groups = [], limit = 6) =>
  groups
    .flatMap(({ streamKey, events = [] }) =>
      events.map((event) => ({
        ...event,
        dashboardStream: streamKey,
        dashboardStreamLabel: DASHBOARD_EVENT_STREAM_LABELS[streamKey] || String(streamKey || "").replace(/[-_]/g, " "),
      })),
    )
    .sort((left, right) => getDashboardEventSortTime(left) - getDashboardEventSortTime(right) || left.title.localeCompare(right.title))
    .slice(0, limit);

export const getDashboardEventSections = ({
  audience = {},
  networkingEvents = [],
  financeEvents = [],
  hrEvents = [],
  managementEvents = [],
  networkingTitle = "Networking Events",
  financeTitle = "Finance Updates - All markets",
  hrTitle = "HR Updates - All markets",
} = {}) => {
  const section = audience.isManagement ? {
    key: "leadership",
    eyebrow: "Upcoming events",
    title: "Marketing, Finance & HR",
    events: managementEvents,
    path: "/calendar?tab=events&stream=all",
    ariaLabel: "Upcoming marketing finance and HR events",
  } : audience.isFinance ? {
    key: "finance",
    eyebrow: "Upcoming finance updates",
    title: financeTitle,
    events: financeEvents,
    path: "/calendar?tab=events&stream=finance",
    ariaLabel: "Upcoming finance updates",
  } : audience.isHr ? {
    key: "hr",
    eyebrow: "Upcoming HR updates",
    title: hrTitle,
    events: hrEvents,
    path: "/calendar?tab=events&stream=hr",
    ariaLabel: "Upcoming HR updates",
  } : {
    key: "marketing",
    eyebrow: "Upcoming networking events",
    title: networkingTitle,
    events: networkingEvents,
    path: "/calendar?tab=events&stream=marketing",
    ariaLabel: "Upcoming networking events",
  };

  return section.events.length > 0 ? [section] : [];
};

const toTitleCase = (value) =>
  String(value || "")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : DATE_FORMAT.format(date);
};

const formatMoney = (value, currency = "USD") => {
  const numberValue = numberOrNull(value);
  if (numberValue === null) return "-";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(numberValue);
  } catch (_error) {
    return `${currency} ${numberValue.toLocaleString("en-US")}`;
  }
};

const toIsoDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const getWeekStart = (date = new Date()) => {
  const result = new Date(date);
  const day = result.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  result.setDate(result.getDate() + diff);
  result.setHours(0, 0, 0, 0);
  return result;
};

const getWeekDays = (date = new Date()) => {
  const start = getWeekStart(date);
  return Array.from({ length: 7 }, (_, index) => {
    const next = new Date(start);
    next.setDate(start.getDate() + index);
    return next;
  });
};

const getLeaveTypeName = (entitlement) =>
  String(
    pickFirst(
      entitlement?.leave_type?.leave_name,
      entitlement?.leave_type?.name,
      entitlement?.leaveType?.name,
      entitlement?.leave_name,
      entitlement?.type,
    ) || "",
  ).toLowerCase();

export const getAnnualLeaveBalance = (entitlements = []) =>
  entitlements.reduce((total, entitlement) => {
    const name = getLeaveTypeName(entitlement);
    if (!ANNUAL_LEAVE_KEYS.some((key) => name.includes(key))) return total;
    if (entitlement?.isExpired === true || entitlement?._isDateRangeValid === false) return total;
    const left = numberOrNull(pickFirst(entitlement.available_now, entitlement.leave_left));
    return total + (left ?? 0);
  }, 0);

export const getLeaveRequestStatus = (request = {}) => {
  if (request?.is_approved === true || request?.is_approved === 1 || request?.is_approved === "1") {
    return "Approved";
  }

  if (request?.is_approved === false || request?.is_approved === 0 || request?.is_approved === "0") {
    return "Rejected";
  }

  return "Need Review";
};

export const getLeaveRequestType = (request = {}) => {
  const attributes = request?.leave_type?.attributes;
  const attributeLabel = attributes && typeof attributes === "object" && !Array.isArray(attributes)
    ? attributes.label
    : null;
  const rawType = pickFirst(
    attributeLabel,
    request?.leave_type?.label,
    request?.leave_type?.leave_name,
    request?.leave_type?.name,
    request?.type,
    request?.leaveName,
  );

  return toTitleCase(rawType).replace(/\s*Year\s*/gi, " ").replace(/\s+/g, " ").trim() || "Leave";
};

export const formatLeaveDuration = (value) => {
  const days = numberOrNull(value);
  if (days === null) return "-";

  return `${days.toLocaleString("en-US", { maximumFractionDigits: 1 })} ${days === 1 ? "day" : "days"}`;
};

const getLeaveRequestDateKey = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) return dateMatch[1];
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : toIsoDate(date);
};

const isApprovedLeaveRequest = (request = {}) =>
  request?.is_approved === true || request?.is_approved === 1 || request?.is_approved === "1";

const isDateCoveredByApprovedLeave = (dateKey, leaveRequests = []) =>
  leaveRequests.some((request) => {
    if (!isApprovedLeaveRequest(request)) return false;

    const startDate = getLeaveRequestDateKey(pickFirst(
      request.start_date,
      request.startDate,
      request.start_at,
      request.date,
    ));
    const endDate = getLeaveRequestDateKey(pickFirst(
      request.end_date,
      request.endDate,
      request.end_at,
      request.date,
    )) || startDate;

    return startDate && endDate && startDate <= dateKey && dateKey <= endDate;
  });

export const getTimesheetWeekSummary = (tasks = [], date = new Date(), leaveRequests = []) => {
  const weekDays = getWeekDays(date);
  const completedDateKeys = new Set(weekDays.filter((day) => {
    const key = toIsoDate(day);
    return tasks.some((task) => Number(task?.timeSpent?.[key] || 0) > 0)
      || isDateCoveredByApprovedLeave(key, leaveRequests);
  }).map(toIsoDate));

  return {
    weekDays,
    completedDateKeys,
    submitted: completedDateKeys.size,
    total: weekDays.length,
  };
};

export const getTeamIncompleteTimesheetCount = (teamMembers = [], weekStart, expectedHours = 40) => {
  const weekKey = typeof weekStart === "string" ? weekStart : toIsoDate(weekStart);
  const weekEnd = new Date(weekKey);
  weekEnd.setDate(weekEnd.getDate() + 6);

  return teamMembers.filter((member) => {
    if (member?.joinDate && new Date(member.joinDate) > weekEnd) return false;
    return numberOrNull(member?.weeks?.[weekKey]) < expectedHours;
  }).length;
};

export const getOpenExpenseRequests = (expenseRequests = []) =>
  expenseRequests.filter((request) => {
    const status = String(request?.status ?? "");
    if (status === "3" && request?.is_approved === false) return false;
    return ["1", "2", "3", "4"].includes(status);
  });

const isTrainingDue = (enrollment) => {
  const status = String(enrollment?.status || "").toLowerCase();
  return status !== "completed" && status !== "done" && status !== "passed";
};

const isAppraisalOpen = (appraisal) => {
  const step = Number(appraisal?.currentStep ?? appraisal?.step ?? 0);
  return !appraisal?.concluded_at && step !== -1 && step < 6;
};

const getProjectClientName = (project) => {
  if (typeof project?.client === "string") return project.client;
  return pickFirst(project?.client?.name, project?.clientName, project?.client_name) || "-";
};

export const getProjectStatusLabel = (project = {}) => {
  if (String(project?.type || "").toUpperCase() === "LEAD") {
    const lead = pickFirst(project.lead, project.leadStatus, project.lead_status);
    if (lead === null) return "-";
    return project.project_lead_status?.name ? `${lead} - ${project.project_lead_status.name}` : String(lead);
  }

  return pickFirst(project.status_name, project.status, project.project_status?.name) || "-";
};

export const getTaskStatusLabel = (task = {}) =>
  getTaskStatusMeta(pickFirst(task.status, task.task_status?.name)).label;

export const formatTaskDueDate = (value) =>
  value ? formatAccountingDate(value, "D MMM YYYY") : "No Due Date";

const EXPENSE_STATUS_LABELS = {
  1: "Draft",
  2: "Pending Approval",
  3: "Approved",
  4: "Finance to Process",
  5: "Processed",
  6: "Archived",
  30: "Rejected",
};

const statusClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("approve") || normalized.includes("complete") || normalized === "processed" || normalized === "live") return "success";
  if (normalized.includes("overdue") || normalized.includes("reject")) return "danger";
  return "warning";
};

const StatusPill = ({ children }) => (
  <span className={`staff-dashboard-status staff-dashboard-status--${statusClass(children)}`}>
    {children || "Pending"}
  </span>
);

const SectionHeader = ({ title, onViewAll }) => (
  <div className="staff-dashboard-section-header">
    <h2>{title}</h2>
    {onViewAll && (
      <button type="button" onClick={onViewAll}>
        <span>View All</span>
        <RightOutlined />
      </button>
    )}
  </div>
);

const EmptyState = ({ children = "Nothing pending." }) => (
  <div className="staff-dashboard-empty">{children}</div>
);

const getProjectShowcaseStorage = () => {
  try {
    return globalThis.localStorage;
  } catch (_error) {
    return null;
  }
};

export const getProjectShowcaseAssetUrl = (assetPath) => {
  if (!assetPath) return null;

  try {
    return new URL(assetPath, PROJECT_SHOWCASE_URL).toString();
  } catch (_error) {
    return assetPath;
  }
};

export const getProjectShowcaseRequestUrl = (email) => {
  const requestUrl = new URL(PROJECT_SHOWCASE_URL);
  const loggedInEmail = String(email || "").trim();

  if (loggedInEmail) requestUrl.searchParams.set("email", loggedInEmail);

  return requestUrl.toString();
};

export const getProjectShowcaseCacheKey = (userId) => {
  const normalizedUserId = String(userId || "").trim();
  return normalizedUserId ? `${PROJECT_SHOWCASE_CACHE_KEY}:${normalizedUserId}` : null;
};

const escapeSvgText = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export const getProjectShowcaseFallbackImageUrl = (project = {}, index = 0) => {
  const palette = PROJECT_SHOWCASE_PALETTES[index % PROJECT_SHOWCASE_PALETTES.length];
  const rawTitle = project.title || "Project";
  const rawDepartment = project.department || project.kind || "Mediatropy";
  const title = escapeSvgText(rawTitle);
  const department = escapeSvgText(rawDepartment.toUpperCase().slice(0, 42));
  const titleFontSize = rawTitle.length > 34 ? 34 : rawTitle.length > 24 ? 40 : 46;
  const initial = escapeSvgText(rawTitle.trim().charAt(0).toUpperCase() || "P");
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 960 540" role="img" aria-label="${title}">`,
    `<rect width="960" height="540" fill="${palette.background}"/>`,
    `<path d="M-80 470 C150 300 260 640 520 420 S840 210 1050 300 V540 H-80 Z" fill="${palette.accent}" opacity="0.22"/>`,
    `<circle cx="785" cy="112" r="88" fill="${palette.accent}" opacity="0.2"/>`,
    `<circle cx="875" cy="270" r="44" fill="${palette.secondary}" opacity="0.16"/>`,
    `<rect x="56" y="64" width="118" height="118" rx="22" fill="${palette.accent}"/>`,
    `<text x="115" y="148" text-anchor="middle" font-family="Avenir, Arial, sans-serif" font-size="78" font-weight="800" fill="#111111">${initial}</text>`,
    `<text x="56" y="298" font-family="Avenir, Arial, sans-serif" font-size="${titleFontSize}" font-weight="800" fill="${palette.secondary}">${title}</text>`,
    `<text x="58" y="346" font-family="Avenir, Arial, sans-serif" font-size="24" font-weight="700" letter-spacing="3" fill="${palette.accent}">${department}</text>`,
    `<rect x="58" y="382" width="260" height="8" rx="4" fill="${palette.accent}"/>`,
    `<rect x="58" y="406" width="168" height="8" rx="4" fill="${palette.secondary}" opacity="0.56"/>`,
    `</svg>`,
  ].join("");

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const getProjectShowcaseArtifactUrl = (artifacts, type) => {
  const artifact = Array.isArray(artifacts)
    ? artifacts.find((item) => String(item?.type || "").toLowerCase() === type && item?.url)
    : null;

  return getProjectShowcaseAssetUrl(artifact?.url);
};

export const normalizeProjectShowcase = (payload = {}) => {
  const remoteProjects = (Array.isArray(payload.projects) ? payload.projects : [])
    .slice(0, PROJECT_SHOWCASE_REMOTE_MAX_ITEMS)
    .map((project, index) => {
      const artifactUrl = project.artifacts?.find((artifact) => /^https?:\/\//.test(artifact?.url || ""))?.url;
      const atlasProjectSlug = project.sourcePageSlug || project.slug;

      return {
        key: project.id || project.slug || project.title || index,
        title: project.title || "Project",
        summary: project.summary || project.narrative || "",
        department: project.department || project.kind || "",
        status: project.status || "",
        sourceUrl: atlasProjectSlug ? `https://atlas.mediatropy.com/projects/${encodeURIComponent(atlasProjectSlug)}` : project.sourceUrl || artifactUrl || "",
        heroUrl: getProjectShowcaseAssetUrl(project.heroAssetPath),
        videoUrl: getProjectShowcaseArtifactUrl(project.artifacts, "video"),
        videoPosterUrl: getProjectShowcaseArtifactUrl(project.artifacts, "video-poster"),
        fallbackHeroUrl: getProjectShowcaseFallbackImageUrl(project, index),
        actionLabel: "View Project",
      };
    });

  return {
    cadence: payload.showcase?.cadence || null,
    weekStart: payload.showcase?.weekStart || null,
    weekEnd: payload.showcase?.weekEnd || null,
    projects: [{ ...EMPLOYEE_HANDBOOK_SHOWCASE_PROJECT }, ...remoteProjects],
  };
};

export const getProjectShowcaseScrollStep = (cardWidth, gap, fallbackWidth) =>
  cardWidth > 0 ? cardWidth + Math.max(gap, 0) : fallbackWidth / PROJECT_SHOWCASE_PAGE_SIZE;

export const getProjectShowcasePosition = (scrollLeft, step, count) => {
  if (step <= 0 || count <= 1) return 0;
  return Math.max(0, Math.min(count - 1, Math.round(scrollLeft / step)));
};

export const getProjectShowcaseVisibleCount = (viewportWidth, step, gap = 0) => {
  if (viewportWidth <= 0 || step <= 0) return PROJECT_SHOWCASE_PAGE_SIZE;
  return Math.max(1, Math.floor((viewportWidth + Math.max(gap, 0) + 0.5) / step));
};

export const getProjectShowcaseDotCount = (itemCount, visibleCount) =>
  Math.max(1, Math.max(Number(itemCount) || 0, 0) - Math.max(Number(visibleCount) || 1, 1) + 1);

export const getUserCountryLabel = (user = {}) => {
  const sourceUser = user || {};
  const rawCountry = pickFirst(
    sourceUser.office && typeof sourceUser.office === "object" ? sourceUser.office.country : null,
    sourceUser.officeCountry,
  );
  if (!rawCountry || rawCountry === "null") return null;

  const countryValue = typeof rawCountry === "object"
    ? pickFirst(rawCountry.name, rawCountry.code)
    : String(rawCountry);
  const country = countries.find((item) =>
    item.code.toLowerCase() === countryValue.toLowerCase() ||
    item.name.toLowerCase() === countryValue.toLowerCase());

  return country?.name || countryValue;
};

export const readProjectShowcaseCache = (
  storage = getProjectShowcaseStorage(),
  now = Date.now(),
  cacheKey = PROJECT_SHOWCASE_CACHE_KEY,
) => {
  if (!storage || !cacheKey) return null;

  try {
    const cached = JSON.parse(storage.getItem(cacheKey));
    if (cached?.expiresAt > now && Array.isArray(cached.data?.projects)) return cached.data;
  } catch (_error) {
    return null;
  }

  return null;
};

export const writeProjectShowcaseCache = (
  data,
  storage = getProjectShowcaseStorage(),
  now = Date.now(),
  cacheKey = PROJECT_SHOWCASE_CACHE_KEY,
) => {
  if (!storage || !cacheKey) return;

  try {
    storage.setItem(cacheKey, JSON.stringify({
      data,
      expiresAt: now + PROJECT_SHOWCASE_CACHE_MS,
    }));
  } catch (_error) {
    // Cache failures should not block the dashboard.
  }
};

const StaffDashboard = ({
  user,
  userTitle,
  userDepartment,
  isTeamLeaderOrAbove = false,
  onNavigate,
}) => {
  const showcaseViewportRef = useRef(null);
  const loggedInEmail = String(user?.email || "").trim();
  const projectShowcaseCacheKey = getProjectShowcaseCacheKey(user?.id);
  const [snapshot, setSnapshot] = useState({
    entitlements: [],
    leaveRequests: [],
    enrollments: [],
    appraisals: [],
    timesheetTasks: [],
    projects: [],
    expenseRequests: [],
    tasks: [],
    teamLeavePending: 0,
    teamAppraisals: [],
    teamTimesheets: [],
  });
  const [projectShowcase, setProjectShowcase] = useState({
    projects: [{ ...EMPLOYEE_HANDBOOK_SHOWCASE_PROJECT }],
  });
  const [showcaseDotCount, setShowcaseDotCount] = useState(1);
  const [showcasePosition, setShowcasePosition] = useState(0);
  const [showcaseStep, setShowcaseStep] = useState(0);
  const [showcaseHoverPaused, setShowcaseHoverPaused] = useState(false);
  const [showcaseInteractionPaused, setShowcaseInteractionPaused] = useState(false);
  const [activeShowcaseVideoUrl, setActiveShowcaseVideoUrl] = useState(null);
  const [unavailableShowcaseVideos, setUnavailableShowcaseVideos] = useState({});
  const [profileUser, setProfileUser] = useState(null);
  const showcasePauseTimerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const today = useMemo(() => new Date(), []);
  const weekSummary = useMemo(
    () => getTimesheetWeekSummary(snapshot.timesheetTasks, today, snapshot.leaveRequests),
    [snapshot.leaveRequests, snapshot.timesheetTasks, today],
  );
  const userCountry = getUserCountryLabel(user) || getUserCountryLabel(profileUser);
  const annualLeaveBalance = getAnnualLeaveBalance(snapshot.entitlements);
  const trainingDue = snapshot.enrollments.filter(isTrainingDue);
  const openAppraisals = snapshot.appraisals.filter(isAppraisalOpen);
  const openExpenseRequests = getOpenExpenseRequests(snapshot.expenseRequests);
  const projectItems = snapshot.projects.slice(0, 3);
  const weekStart = weekSummary.weekDays[0];
  const weekEnd = weekSummary.weekDays[6];
  const teamAppraisalReviews = snapshot.teamAppraisals.filter(isAppraisalOpen).length;
  const teamTimesheetIncomplete = getTeamIncompleteTimesheetCount(snapshot.teamTimesheets, weekStart);
  const dashboardEventAudience = getDashboardEventAudience(user, userDepartment);
  const isManagementDashboard = dashboardEventAudience.isManagement;
  const upcomingNetworkingEvents = getUpcomingNetworkingEvents({
    limit: isManagementDashboard ? 2 : 5,
    country: userCountry,
  });
  const upcomingFinanceEvents = getUpcomingFinanceUpdateEvents({ limit: isManagementDashboard ? 2 : 5 });
  const upcomingHrEvents = getUpcomingHrUpdateEvents({ limit: isManagementDashboard ? 2 : 5 });
  const networkingEventsTitle = userCountry ? `Networking Events - ${userCountry}` : "Networking Events";
  const financeEventsTitle = "Finance Updates - All markets";
  const hrEventsTitle = "HR Updates - All markets";
  const managementDashboardEvents = getCombinedDashboardEvents([
    { streamKey: "marketing", events: upcomingNetworkingEvents },
    { streamKey: "finance", events: upcomingFinanceEvents },
    { streamKey: "hr", events: upcomingHrEvents },
  ]);
  const dashboardEventSections = getDashboardEventSections({
    audience: dashboardEventAudience,
    networkingEvents: upcomingNetworkingEvents,
    financeEvents: upcomingFinanceEvents,
    hrEvents: upcomingHrEvents,
    managementEvents: managementDashboardEvents,
    networkingTitle: networkingEventsTitle,
    financeTitle: financeEventsTitle,
    hrTitle: hrEventsTitle,
  });

  useEffect(() => {
    let cancelled = false;

    const loadStaffDashboard = async () => {
      if (!user?.id) return;
      setLoading(true);

      const week = toIsoDate(getWeekStart(new Date()));
      const weekEndDate = new Date(week);
      weekEndDate.setDate(weekEndDate.getDate() + 6);
      const requests = await Promise.allSettled([
        nodeAPI.get(`/leave/entitlements/${user.id}`),
        fetchLeaveRequestHistory(nodeAPI, user.id),
        nodeAPI.get("/training/courses/enrolled"),
        nodeAPI.get(`/appraisals/user/${user.id}`),
        nodeAPI.get("/timesheet/weekly", { params: { week } }),
        nodeAPI.get("/project/my-projects", { params: { page: 1, limit: 3, status: "all", summary: true } }),
        nodeAPI.get("/finance/expense-requests", { params: { page: 1, limit: 100, isAdmin: false } }),
        nodeAPI.get(`${API_CHECKLIST_TASK}/unified?taskType=assigned&limit=3&includeCompleted=false`),
        ...(isTeamLeaderOrAbove ? [
          nodeAPI.get("/leave/pending-count"),
          nodeAPI.get(`/appraisals/reviewer/${user.id}`),
          nodeAPI.get("/timesheet/team", {
            params: {
              startDate: week,
              endDate: toIsoDate(weekEndDate),
              managerId: user.id,
              scope: "direct",
            },
          }),
        ] : []),
      ]);

      if (cancelled) return;

      const [
        entitlements,
        leaveRequests,
        enrollments,
        appraisals,
        timesheet,
        projects,
        expenseRequests,
        tasks,
        teamLeave,
        teamAppraisals,
        teamTimesheets,
      ] =
        requests.map((result) => result.status === "fulfilled" ? result.value : null);

      requests.forEach((result) => {
        if (result.status === "rejected") {
          logger.warn("Staff dashboard partial load failed:", result.reason);
        }
      });

      setSnapshot({
        entitlements: pickList(entitlements, [
          ["data", "data", "content", "data"],
          ["data", "data"],
        ]),
        leaveRequests: pickList(leaveRequests, [
          ["data", "data", "content", "data"],
          ["data", "data"],
        ]),
        enrollments: pickList(enrollments, [
          ["data", "data", "data"],
          ["data", "data"],
          ["data", "courses"],
        ]),
        appraisals: pickList(appraisals, [
          ["data", "data", "appraisals"],
          ["data", "data", "items"],
          ["data", "data"],
        ]),
        timesheetTasks: pickList(timesheet, [
          ["data", "data", "tasks"],
          ["data", "tasks"],
        ]),
        projects: pickList(projects, [
          ["data", "data", "projects"],
          ["data", "projects"],
        ]),
        expenseRequests: pickList(expenseRequests, [
          ["data", "data", "expenseRequests"],
          ["data", "expenseRequests"],
        ]),
        tasks: pickList(tasks, [
          ["data", "data", "tasks"],
          ["data", "data"],
        ]),
        teamLeavePending: numberOrNull(pickFirst(teamLeave?.data?.data?.count, teamLeave?.data?.count)) ?? 0,
        teamAppraisals: pickList(teamAppraisals, [
          ["data", "data", "items"],
          ["data", "data", "appraisals"],
          ["data", "data"],
        ]),
        teamTimesheets: pickList(teamTimesheets, [
          ["data", "data", "teamMembers"],
          ["data", "teamMembers"],
        ]),
      });
      setLoading(false);
    };

    loadStaffDashboard();

    return () => {
      cancelled = true;
    };
  }, [isTeamLeaderOrAbove, user?.id]);

  useEffect(() => {
    setProfileUser(null);
    if (!user?.id || getUserCountryLabel(user)) return undefined;

    let cancelled = false;

    const loadProfileUser = async () => {
      try {
        const response = await nodeAPI.get("/profile");
        if (!cancelled) setProfileUser(response?.data?.data?.user || null);
      } catch (error) {
        logger.warn("Staff dashboard profile country load failed:", error);
      }
    };

    loadProfileUser();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!loggedInEmail) {
      setProjectShowcase({ projects: [{ ...EMPLOYEE_HANDBOOK_SHOWCASE_PROJECT }] });
      return undefined;
    }

    const cached = readProjectShowcaseCache(undefined, undefined, projectShowcaseCacheKey);
    if (cached) {
      setProjectShowcase(cached);
      return undefined;
    }

    let cancelled = false;

    const loadProjectShowcase = async () => {
      if (typeof fetch !== "function") return;

      try {
        const response = await fetch(getProjectShowcaseRequestUrl(loggedInEmail));
        if (!response.ok) throw new Error(`Project showcase failed: ${response.status}`);

        const nextShowcase = normalizeProjectShowcase(await response.json());
        writeProjectShowcaseCache(nextShowcase, undefined, undefined, projectShowcaseCacheKey);
        if (!cancelled) setProjectShowcase(nextShowcase);
      } catch (error) {
        logger.warn("Project showcase load failed:", error);
      }
    };

    loadProjectShowcase();

    return () => {
      cancelled = true;
    };
  }, [loggedInEmail, projectShowcaseCacheKey]);

  const getShowcaseMetrics = useCallback(() => {
    const viewport = showcaseViewportRef.current;
    if (!viewport) return { step: 0, count: 1 };

    const card = viewport.querySelector(".staff-dashboard-showcase-card");
    const track = viewport.querySelector(".staff-dashboard-showcase-track");
    const trackStyles = track && typeof globalThis.getComputedStyle === "function"
      ? globalThis.getComputedStyle(track)
      : null;
    const gap = Number.parseFloat(trackStyles?.columnGap || trackStyles?.gap || "0") || 0;
    const step = getProjectShowcaseScrollStep(card?.getBoundingClientRect().width || 0, gap, viewport.clientWidth);
    const itemCount = track?.querySelectorAll(".staff-dashboard-showcase-card").length || 0;
    const visibleCount = getProjectShowcaseVisibleCount(viewport.clientWidth, step, gap);
    const count = getProjectShowcaseDotCount(itemCount, visibleCount);

    return { step, count };
  }, []);

  useEffect(() => {
    const refreshShowcaseDots = () => {
      const { count, step } = getShowcaseMetrics();
      setShowcaseStep(step);
      setShowcaseDotCount(count);
      setShowcasePosition((position) => Math.min(position, count - 1));
    };

    refreshShowcaseDots();
    const viewport = showcaseViewportRef.current;
    const resizeObserver = typeof globalThis.ResizeObserver === "function" && viewport
      ? new globalThis.ResizeObserver(refreshShowcaseDots)
      : null;

    resizeObserver?.observe(viewport);
    globalThis.addEventListener?.("resize", refreshShowcaseDots);

    return () => {
      resizeObserver?.disconnect();
      globalThis.removeEventListener?.("resize", refreshShowcaseDots);
    };
  }, [getShowcaseMetrics, projectShowcase.projects.length]);

  const leaveRows = snapshot.leaveRequests.slice(0, 3).map((request, index) => ({
    key: request.id || index,
    date: formatDate(pickFirst(request.start_at, request.startAt, request.start_date, request.date)),
    type: getLeaveRequestType(request),
    duration: formatLeaveDuration(pickFirst(request.duration, request.days, request.number_of_days, request.total_days)),
    status: getLeaveRequestStatus(request),
  }));

  const projectRows = projectItems.map((project, index) => ({
    key: project.id || index,
    project: pickFirst(project.name, project.projectName, project.title) || "Project",
    client: getProjectClientName(project),
    type: toTitleCase(pickFirst(project.type, project.project_type, project.classification?.name)) || "-",
    status: getProjectStatusLabel(project),
  }));

  const expenseRows = snapshot.expenseRequests.slice(0, 3).map((request, index) => ({
    key: request.id || index,
    requestNo: request.generated_id || `#${request.id}`,
    title: request.title || "Expense request",
    amount: formatMoney(request.amount, request.currency?.iso || "USD"),
    payment: formatDate(request.payment_scheduled),
    status: EXPENSE_STATUS_LABELS[request.status] || request.status || "Pending",
  }));

  const taskRows = snapshot.tasks.slice(0, 3).map((task, index) => ({
    key: task.id || index,
    task: pickFirst(task.name, task.title, task.task_name) || "Task",
    dueDate: formatTaskDueDate(pickFirst(task.due_date, task.dueDate)),
    status: getTaskStatusLabel(task),
  }));

  const appraisalRows = openAppraisals.slice(0, 3).map((appraisal, index) => {
    const step = Number(appraisal?.currentStep ?? appraisal?.step ?? 0);
    const progress = Math.max(0, Math.min(100, Math.round((step / 5) * 100))) || 0;

    return {
      key: appraisal.id || index,
      id: appraisal.id,
      title: appraisal.title || "Yearly Appraisal",
      progress,
      dueDate: formatDate(appraisal.submit_at),
      status: "Due",
    };
  });

  const mobileHiddenColumn = { responsive: ["sm"] };

  const leaveColumns = [
    { title: "Date", dataIndex: "date", key: "date" },
    { title: "Type", dataIndex: "type", key: "type", ...mobileHiddenColumn },
    { title: "Duration", dataIndex: "duration", key: "duration" },
    { title: "Status", dataIndex: "status", key: "status", render: (status) => <StatusPill>{status}</StatusPill> },
  ];

  const expenseColumns = [
    { title: "Request No", dataIndex: "requestNo", key: "requestNo", ...mobileHiddenColumn },
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Amount", dataIndex: "amount", key: "amount" },
    { title: "Payment", dataIndex: "payment", key: "payment", ...mobileHiddenColumn },
    { title: "Status", dataIndex: "status", key: "status", render: (status) => <StatusPill>{status}</StatusPill> },
  ];

  const projectColumns = [
    { title: "Project", dataIndex: "project", key: "project" },
    { title: "Client", dataIndex: "client", key: "client", ...mobileHiddenColumn },
    { title: "Type", dataIndex: "type", key: "type", ...mobileHiddenColumn },
    { title: "Status", dataIndex: "status", key: "status", render: (status) => <StatusPill>{status}</StatusPill> },
  ];

  const taskColumns = [
    { title: "Task", dataIndex: "task", key: "task" },
    { title: "Due Date", dataIndex: "dueDate", key: "dueDate" },
    { title: "Status", dataIndex: "status", key: "status", render: (status) => <StatusPill>{status}</StatusPill> },
  ];

  const appraisalColumns = [
    {
      title: "Appraisal",
      dataIndex: "title",
      key: "title",
      render: (title, row) => (
        <button
          type="button"
          className="staff-dashboard-table-link"
          onClick={() => onNavigate(row.id ? `/appraisal/${row.id}` : "/appraisal")}
        >
          {title}
        </button>
      ),
    },
    {
      title: "Progress",
      dataIndex: "progress",
      key: "progress",
      responsive: ["sm"],
      render: (progress) => (
        <div className="staff-dashboard-progress-cell">
          <div className="staff-dashboard-progress">
            <span style={{ width: `${progress}%` }} />
          </div>
          <small>{progress}%</small>
        </div>
      ),
    },
    { title: "Due", dataIndex: "dueDate", key: "dueDate" },
    { title: "Status", dataIndex: "status", key: "status", render: (status) => <StatusPill>{status}</StatusPill> },
  ];

  const summaryCards = [
    {
      key: "leave",
      icon: <CalendarOutlined />,
      label: "Leave",
      value: Number.isFinite(annualLeaveBalance) ? annualLeaveBalance : "-",
      unit: pluralize(annualLeaveBalance, "day"),
      note: `Until ${formatDate(new Date(today.getFullYear(), 11, 31))}`,
      action: isTeamLeaderOrAbove && snapshot.teamLeavePending > 0 && {
        label: getPendingActionLabel(snapshot.teamLeavePending, "approval"),
        path: "/admin/hr/leaves?tab=request",
      },
    },
    {
      key: "training",
      icon: <BookOutlined />,
      label: "Training",
      value: trainingDue.length,
      unit: pluralize(trainingDue.length, "course"),
      note: "Assigned to you",
    },
    {
      key: "appraisal",
      icon: <StarOutlined />,
      label: "Appraisal",
      value: openAppraisals.length,
      unit: pluralize(openAppraisals.length, "appraisal"),
      note: "Needs action",
      action: isTeamLeaderOrAbove && teamAppraisalReviews > 0 && {
        label: getPendingActionLabel(teamAppraisalReviews, "review"),
        path: "/appraisal?tab=team",
      },
    },
    {
      key: "expenses",
      icon: <DollarOutlined />,
      label: "Expenses",
      value: openExpenseRequests.length,
      unit: "open",
      note: "In progress",
    },
    {
      key: "timesheet",
      icon: <ClockCircleOutlined />,
      label: "Timesheet",
      value: `${weekSummary.submitted}/${weekSummary.total}`,
      unit: "days",
      note: `Week of ${formatDate(weekStart)}`,
      action: isTeamLeaderOrAbove && teamTimesheetIncomplete > 0 && {
        label: `${teamTimesheetIncomplete} ${pluralize(teamTimesheetIncomplete, "member")} incomplete`,
        path: "/timesheet?tab=team-timesheets",
      },
    },
    {
      key: "projects",
      icon: <ProjectOutlined />,
      label: "Projects",
      value: projectItems.length,
      unit: pluralize(projectItems.length, "project"),
      note: "Active assignments",
    },
  ];
  const showcaseWeekLabel = projectShowcase.weekStart && projectShowcase.weekEnd
    ? `${formatDate(projectShowcase.weekStart)} - ${formatDate(projectShowcase.weekEnd)}`
    : null;
  const canScrollShowcase = showcaseDotCount > 1;
  const pauseProjectShowcase = useCallback(() => {
    if (showcasePauseTimerRef.current) globalThis.clearTimeout?.(showcasePauseTimerRef.current);
    setShowcaseInteractionPaused(true);
    showcasePauseTimerRef.current = globalThis.setTimeout?.(() => {
      setShowcaseInteractionPaused(false);
      showcasePauseTimerRef.current = null;
    }, PROJECT_SHOWCASE_INTERACTION_PAUSE_MS);
  }, []);

  const startProjectShowcasePreview = (event) => {
    const video = event.currentTarget.querySelector(".staff-dashboard-showcase-video");
    if (!video) return;

    video.muted = true;
    const playPromise = video?.play();

    if (typeof playPromise?.catch === "function") playPromise.catch(() => undefined);
  };

  const stopProjectShowcasePreview = (event, videoUrl) => {
    const video = event.currentTarget.querySelector(".staff-dashboard-showcase-video");
    if (!video) return;

    video.pause();
    try {
      video.currentTime = 0;
    } catch (_error) {
      // The video may not have enough metadata to reset yet.
    }
    setActiveShowcaseVideoUrl((current) => current === videoUrl ? null : current);
  };

  useEffect(() => () => {
    if (showcasePauseTimerRef.current) globalThis.clearTimeout?.(showcasePauseTimerRef.current);
  }, []);

  const goToProjectShowcasePosition = (position) => {
    const { count } = getShowcaseMetrics();
    const nextPosition = Math.max(0, Math.min(position, count - 1));
    setShowcasePosition(nextPosition);
  };
  const scrollProjectShowcase = (direction) => {
    goToProjectShowcasePosition(showcasePosition + direction);
  };

  useEffect(() => {
    if (showcaseHoverPaused || showcaseInteractionPaused || activeShowcaseVideoUrl || !canScrollShowcase || showcaseDotCount <= 1) return undefined;

    const timer = globalThis.setInterval?.(() => {
      setShowcasePosition((currentPosition) => {
        return (currentPosition + 1) % showcaseDotCount;
      });
    }, PROJECT_SHOWCASE_AUTO_SLIDE_MS);

    return () => globalThis.clearInterval?.(timer);
  }, [activeShowcaseVideoUrl, canScrollShowcase, getShowcaseMetrics, showcaseDotCount, showcaseHoverPaused, showcaseInteractionPaused]);

  return (
    <main className="staff-dashboard">
      <div className="staff-dashboard-header">
        <div>
          <h1>Welcome Back, {user?.name || user?.email || "there"}</h1>
          <div className="staff-dashboard-user-meta">
            {[userTitle, userDepartment, userCountry].filter(Boolean).map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>
        <div className="staff-dashboard-header-actions">
          <Button
            variant="secondary"
            size="small"
            uppercase={false}
            className="staff-dashboard-feedback-action"
            icon={<MessageOutlined />}
            aria-label="Share Workplace Feedback with HR"
            title="Share Workplace Feedback with HR"
            onClick={() => onNavigate("/workplace-feedback")}
          >
            Share Workplace Feedback with HR
          </Button>
          <Button
            variant="secondary"
            size="small"
            uppercase={false}
            className="staff-dashboard-header-shortcut"
            icon={<QuestionCircleOutlined />}
            aria-label="Open Q&A"
            title="Q&A"
            onClick={() => onNavigate("/qa")}
          />
          <DashboardNotificationBell onNavigate={onNavigate} />
          <button
            type="button"
            className="staff-dashboard-date"
            aria-label="Open calendar"
            title="Calendar"
            onClick={() => onNavigate("/calendar")}
          >
            <CalendarOutlined />
            <div>
              <strong>{LONG_DATE_FORMAT.format(today)}</strong>
              <span>{TIME_FORMAT.format(today)}</span>
            </div>
          </button>
        </div>
      </div>

      {dashboardEventSections.length > 0 && (
        <div className="staff-dashboard-events-stack">
          {dashboardEventSections.map((section) => (
            <section
              key={section.key}
              className={`staff-dashboard-events-band staff-dashboard-events-band--${section.key}`}
              aria-label={section.ariaLabel}
            >
              <div className="staff-dashboard-events-band-header">
                <div>
                  <span>{section.eyebrow}</span>
                  <strong>{section.title}</strong>
                </div>
                <button type="button" onClick={() => onNavigate(section.path)}>
                  <span>View All</span>
                  <RightOutlined />
                </button>
              </div>
              <div className="staff-dashboard-events-band-list">
                {section.events.map((event) => (
                  <a
                    key={event.id}
                    href={event.registrationUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="staff-dashboard-events-band-item"
                  >
                    <span>{formatNetworkingEventDateRange(event)}</span>
                    <strong>{event.title}</strong>
                    <small>{[event.dashboardStreamLabel, event.market, event.type].filter(Boolean).join(" - ")}</small>
                    <em>{event.ctaLabel || "Register here"}</em>
                  </a>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <section className="staff-dashboard-summary" aria-label="Dashboard summary">
        {summaryCards.map((card) => (
          <div key={card.key} className="staff-dashboard-summary-card">
            <button
              type="button"
              className="staff-dashboard-summary-main"
              onClick={() => {
                const paths = {
                  leave: "/leaves?tab=entitlements",
                  training: "/training",
                  appraisal: "/appraisal",
                  expenses: "/finances?tab=expense-requests",
                  timesheet: "/timesheet",
                  projects: "/project-management",
                };
                onNavigate(paths[card.key]);
              }}
            >
              <span className="staff-dashboard-summary-icon">{card.icon}</span>
              <span className="staff-dashboard-summary-copy">
                <span className="staff-dashboard-summary-label">{card.label}</span>
                <strong>{card.value} <small>{card.unit}</small></strong>
                <span>{card.note}</span>
              </span>
            </button>
            {card.action && (
              <button
                type="button"
                className="staff-dashboard-summary-action"
                onClick={() => onNavigate(card.action.path)}
              >
                <ExclamationCircleOutlined />
                {card.action.label}
              </button>
            )}
          </div>
        ))}
      </section>

      {projectShowcase.projects.length > 0 && (
        <section className="staff-dashboard-showcase">
          <div className="staff-dashboard-showcase-heading">
            <div>
              <SectionHeader title="Project Showcase" />
              {showcaseWeekLabel && <span className="staff-dashboard-showcase-week">{showcaseWeekLabel}</span>}
            </div>
            {canScrollShowcase && (
              <div className="staff-dashboard-showcase-controls">
                <button
                  type="button"
                  aria-label="Previous projects"
                  onClick={() => {
                    pauseProjectShowcase();
                    scrollProjectShowcase(-1);
                  }}
                >
                  <LeftOutlined />
                </button>
                <button
                  type="button"
                  aria-label="Next projects"
                  onClick={() => {
                    pauseProjectShowcase();
                    scrollProjectShowcase(1);
                  }}
                >
                  <RightOutlined />
                </button>
              </div>
            )}
          </div>
          <div
            className="staff-dashboard-showcase-viewport"
            ref={showcaseViewportRef}
            onMouseEnter={() => setShowcaseHoverPaused(true)}
            onMouseLeave={() => setShowcaseHoverPaused(false)}
            onFocus={() => setShowcaseHoverPaused(true)}
            onBlur={() => setShowcaseHoverPaused(false)}
            onMouseDown={pauseProjectShowcase}
          >
            <div
              className="staff-dashboard-showcase-track"
              style={{ transform: `translate3d(-${showcasePosition * showcaseStep}px, 0, 0)` }}
            >
              {projectShowcase.projects.map((project) => (
                <article
                  className={[
                    "staff-dashboard-showcase-card",
                    project.videoUrl && !unavailableShowcaseVideos[project.videoUrl] && "staff-dashboard-showcase-card--has-video",
                    project.videoUrl && !unavailableShowcaseVideos[project.videoUrl] && activeShowcaseVideoUrl === project.videoUrl && "is-video-playing",
                  ].filter(Boolean).join(" ")}
                  key={project.key}
                  onMouseEnter={project.videoUrl && !unavailableShowcaseVideos[project.videoUrl] ? startProjectShowcasePreview : undefined}
                  onMouseLeave={project.videoUrl && !unavailableShowcaseVideos[project.videoUrl] ? (event) => stopProjectShowcasePreview(event, project.videoUrl) : undefined}
                >
                  <div className="staff-dashboard-showcase-visual">
                    <span>{project.title.charAt(0)}</span>
                    {project.status && <StatusPill>{project.status}</StatusPill>}
                    {(project.heroUrl || project.fallbackHeroUrl) && (
                      <img
                        src={project.heroUrl || project.fallbackHeroUrl}
                        alt=""
                        loading="lazy"
                        onError={(event) => {
                          if (project.fallbackHeroUrl && event.currentTarget.src !== project.fallbackHeroUrl) {
                            event.currentTarget.src = project.fallbackHeroUrl;
                            return;
                          }
                          event.currentTarget.hidden = true;
                        }}
                      />
                    )}
                    {project.videoUrl && !unavailableShowcaseVideos[project.videoUrl] && (
                      <video
                        className="staff-dashboard-showcase-video"
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        poster={project.videoPosterUrl || project.heroUrl || project.fallbackHeroUrl}
                        aria-hidden="true"
                        tabIndex={-1}
                        onPlay={() => {
                          pauseProjectShowcase();
                          setActiveShowcaseVideoUrl(project.videoUrl);
                        }}
                        onPause={() => setActiveShowcaseVideoUrl((current) => current === project.videoUrl ? null : current)}
                        onError={() => {
                          setUnavailableShowcaseVideos((current) => ({ ...current, [project.videoUrl]: true }));
                          setActiveShowcaseVideoUrl((current) => current === project.videoUrl ? null : current);
                        }}
                      >
                        <source src={project.videoUrl} type="video/mp4" />
                      </video>
                    )}
                  </div>
                  <div className="staff-dashboard-showcase-copy">
                    <h3>{project.title}</h3>
                    <p>{project.summary}</p>
                    <div className="staff-dashboard-showcase-footer">
                      <div className="staff-dashboard-showcase-meta">
                        {project.department && <span>{project.department}</span>}
                      </div>
                      {project.sourceUrl && (
                        <a href={project.sourceUrl} target="_blank" rel="noreferrer">
                          {project.actionLabel || "View Project"} <RightOutlined />
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
          {canScrollShowcase && (
            <div className="staff-dashboard-showcase-dots" aria-label="Project showcase position">
              {Array.from({ length: showcaseDotCount }, (_, index) => (
                <button
                  key={index}
                  type="button"
                  className={index === showcasePosition ? "is-active" : undefined}
                  aria-label={`Show project ${index + 1}`}
                  aria-current={index === showcasePosition ? "true" : undefined}
                  onClick={() => {
                    pauseProjectShowcase();
                    goToProjectShowcasePosition(index);
                  }}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {loading && (
        <div className="staff-dashboard-loading">
          <Spin />
        </div>
      )}

      <section className="staff-dashboard-grid">
        <article className="staff-dashboard-panel staff-dashboard-panel--wide">
          <SectionHeader title="Holiday & Leave" onViewAll={() => onNavigate("/leaves?tab=annual-leave")} />
            <div className="staff-dashboard-leave">
              <div className="staff-dashboard-balance">
              <span>Leave Balance</span>
              <strong>{annualLeaveBalance}</strong>
              <small>days as of {formatDate(today)}</small>
            </div>
            <div className="staff-dashboard-table-wrap">
              {leaveRows.length ? (
                <Table
                  variant="standard"
                  borderless
                  pagination={false}
                  size="small"
                  scroll={DASHBOARD_TABLE_SCROLL}
                  columns={leaveColumns}
                  dataSource={leaveRows}
                />
              ) : (
                <EmptyState>No leave requests found.</EmptyState>
              )}
            </div>
          </div>
          <div className="staff-dashboard-panel-action">
            <Button
              size="small"
              uppercase={false}
              icon={<PlusOutlined />}
              onClick={() => onNavigate("/leaves?tab=annual-leave&action=new&filter=annual")}
            >
              Request Leave
            </Button>
          </div>
        </article>

        <article className="staff-dashboard-panel staff-dashboard-panel--wide">
          <SectionHeader title="Appraisal" onViewAll={() => onNavigate("/appraisal")} />
          {appraisalRows.length ? (
            <Table
              variant="standard"
              borderless
              pagination={false}
              size="small"
              scroll={DASHBOARD_TABLE_SCROLL}
              columns={appraisalColumns}
              dataSource={appraisalRows}
            />
          ) : (
            <EmptyState>No appraisal action needed.</EmptyState>
          )}
        </article>

        <article className="staff-dashboard-panel">
          <SectionHeader title="Training To Do" onViewAll={() => onNavigate("/training")} />
          {trainingDue.length ? (
            <div className="staff-dashboard-training-list">
              {trainingDue.slice(0, 3).map((enrollment) => {
                const course = enrollment.course || enrollment;
                return (
                  <div className="staff-dashboard-training-card" key={enrollment.id || course.id}>
                    <span><BookOutlined /></span>
                    <strong>{course.title || course.name || "Training course"}</strong>
                    <small>{course.type || "e-Learning"} · {course.duration || "Self-paced"}</small>
                    <Button
                      variant="secondary"
                      size="small"
                      uppercase={false}
                      icon={<RightOutlined />}
                      onClick={() => onNavigate(course.id ? `/course/${course.id}` : "/training")}
                    >
                      Take Course
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState>No training due.</EmptyState>
          )}
        </article>

        <article className="staff-dashboard-panel">
          <SectionHeader title="My Expense Requests" onViewAll={() => onNavigate("/finances?tab=expense-requests")} />
          {expenseRows.length ? (
            <Table
              variant="standard"
              borderless
              pagination={false}
              size="small"
              scroll={DASHBOARD_TABLE_SCROLL}
              columns={expenseColumns}
              dataSource={expenseRows}
            />
          ) : (
            <EmptyState>No expense requests found.</EmptyState>
          )}
          <div className="staff-dashboard-panel-action">
            <Button
              size="small"
              uppercase={false}
              icon={<PlusOutlined />}
              onClick={() => onNavigate("/finances/expense-requests/new")}
            >
              New Request
            </Button>
          </div>
        </article>

        <article className="staff-dashboard-panel">
          <SectionHeader title="My Tasks" onViewAll={() => onNavigate("/tasks/my-tasks")} />
          {taskRows.length ? (
            <Table
              variant="standard"
              borderless
              pagination={false}
              size="small"
              scroll={DASHBOARD_TABLE_SCROLL}
              columns={taskColumns}
              dataSource={taskRows}
            />
          ) : (
            <EmptyState>No active tasks assigned.</EmptyState>
          )}
        </article>

        <article className="staff-dashboard-panel">
          <SectionHeader title="My Projects" onViewAll={() => onNavigate("/project-management")} />
          {projectRows.length ? (
            <Table
              variant="standard"
              borderless
              pagination={false}
              size="small"
              scroll={DASHBOARD_TABLE_SCROLL}
              columns={projectColumns}
              dataSource={projectRows}
            />
          ) : (
            <EmptyState>No projects found.</EmptyState>
          )}
        </article>

        <article className="staff-dashboard-panel">
          <SectionHeader title="Timesheet" onViewAll={() => onNavigate("/timesheet")} />
          <div className="staff-dashboard-timesheet">
            <div>
              <span>Week</span>
              <strong>{formatDate(weekStart)} - {formatDate(weekEnd)}</strong>
              <b>{weekSummary.submitted}/{weekSummary.total}</b>
              <small>days submitted</small>
            </div>
            <div className="staff-dashboard-week-dots">
              {weekSummary.weekDays.map((day) => {
                const key = toIsoDate(day);
                const complete = weekSummary.completedDateKeys.has(key);
                return (
                  <span key={key}>
                    <small>{day.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 1)}</small>
                    {complete ? <CheckCircleFilled /> : <i />}
                  </span>
                );
              })}
            </div>
            <Button size="small" uppercase={false} icon={<RightOutlined />} onClick={() => onNavigate("/timesheet")}>
              Fill Timesheet
            </Button>
          </div>
        </article>
      </section>

      <p className="staff-dashboard-footnote">
        <FolderOpenOutlined /> All data shown is scoped to your account.
      </p>
    </main>
  );
};

export default StaffDashboard;
